/**
 * @deprecated Use AuthGuard component or useRequireAuth hook instead.
 *
 * This HOC is maintained for backward compatibility.
 * New code should use:
 * - <AuthGuard> component for JSX usage
 * - useRequireAuth() hook for programmatic access
 *
 * @example
 * // Instead of:
 * const ProtectedPage = withAuth(MyPage);
 *
 * // Use:
 * import { AuthGuard } from '../components/auth';
 * <AuthGuard><MyPage /></AuthGuard>
 *
 * // Or with hook:
 * import { useRequireAuth } from '../hooks/auth';
 * const { user, isReady } = useRequireAuth();
 */

import { useRouter } from "next/router";
import { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";

/**
 * @param {React.Component} Component
 * @returns {React.Component}
 * @deprecated Use AuthGuard or useRequireAuth instead
 */
const withAuth = (Component) => {
  const AuthenticatedComponent = (props) => {
    const { user, loading, isAuthenticated } = useAuth();
    const router = useRouter();

    useEffect(() => {
      if (!loading) {
        const authenticated = isAuthenticated();
        // Redirect to sign-in if not authenticated after checking auth status
        if (!authenticated) {
          console.log(
            "withAuth redirecting: loading=",
            loading,
            "isAuthenticated=",
            authenticated,
            "user=",
            user,
          );
          router.replace({
            pathname: "/sign-in",
            query: { returnUrl: router.asPath },
          });
        }
      }
    }, [loading, user, isAuthenticated, router]);

    // Show loading indicator while checking auth status or if user is not authenticated yet
    if (loading || (!loading && !isAuthenticated())) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
        </div>
      );
    }

    // If authenticated, render the protected component
    return <Component {...props} />;
  };

  return AuthenticatedComponent;
};

export default withAuth;
